import React, { createContext, useContext, useReducer, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from './AuthContext';
import api from '../api/api';
import type { Topping, SpiceLevel } from '../types/menu';
import { useCustomerDisplayBroadcast } from '../edge/useCustomerDisplayBroadcast';

interface OrderMenuItem {
  id: number;
  name?: string;
  price?: number;
}

interface OrderTopping {
  id: number;
  name?: string;
}

interface OrderItem {
  menuItem: OrderMenuItem;
  quantity: number;
  price: number;
  toppings?: OrderTopping[];
  comment?: string;
}

interface Order {
  id: number;
  items: OrderItem[];
  total?: number;
  status?: string;
}

interface CartItem {
  menuItem: { id: number; name?: string; dietaryType?: string };
  quantity: number;
  price: number;
  portionId?: number;
  portionName?: string;
  toppings?: number[];
  selectedToppings?: Topping[];
  freeToppingsCount?: number;
  requestedSpiceLevel?: SpiceLevel;
  comment?: string;
}

interface CartState {
  items: CartItem[];
  qrCode: string | null;
  vendorId: string | null;
  vendorName?: string | null;
  pendingOrderId?: string | null;
}

interface CartContextType {
  state: CartState;
  addToCart: (item: {
    id: number;
    name?: string;
    price: number;
    portionId?: number;
    portionName?: string;
    selectedToppings?: Topping[];
    freeToppingsCount?: number;
    requestedSpiceLevel?: SpiceLevel;
    dietaryType?: string;
  }, quantity?: number) => void;
  removeFromCart: (itemId: number) => void;
  updateQuantity: (itemId: number, quantity: number) => void;
  updateItemComment: (itemId: number, comment: string) => void;
  updateItemToppings: (itemId: number, toppings: number[]) => void;
  updateItemPrice: (itemId: number, price: number) => void;
  clearCart: () => void;
  getCartQuantity: (itemId: number, portionId?: number) => number;
  getTotalItems: () => number;
  isCartValid: () => boolean;
  clearVendorContext: () => Promise<void>;
  setVendorContext: (vendorId: number, vendorName: string, qrCode: string) => void;
  hydrateCartFromOrder: (order: Order) => void;
  setPendingOrderId: (orderId: string | null) => void;
  clearCartAfterOrderPlacement: (orderId: string) => void;
  getCartTotal: () => number;
  getItemCount: () => number;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

type CartAction =
  | { type: 'ADD_ITEM'; payload: CartItem }
  | { type: 'REMOVE_ITEM'; payload: { itemId: number } }
  | { type: 'UPDATE_QUANTITY'; payload: { itemId: number; quantity: number } }
  | { type: 'UPDATE_COMMENT'; payload: { itemId: number; comment: string } }
  | { type: 'UPDATE_TOPPINGS'; payload: { itemId: number; toppings: number[] } }
  | { type: 'UPDATE_PRICE'; payload: { itemId: number; price: number } }
  | { type: 'CLEAR_CART' }
  | { type: 'SET_QR_CODE'; payload: { qrCode: string | null } }
  | { type: 'SET_VENDOR_ID'; payload: { vendorId: string | null } }
  | { type: 'SET_VENDOR_CONTEXT'; payload: { vendorId: string; vendorName: string; qrCode: string } }
  | { type: 'SET_PENDING_ORDER_ID'; payload: { orderId: string | null } }
  | { type: 'HYDRATE_FROM_ORDER'; payload: Order }
  | { type: 'INITIALIZE_FROM_STORAGE'; payload: CartState };

// localStorage key for cart items
const CART_STORAGE_KEY = 'cart-items';

// Helper function to save cart to localStorage
const saveCartToStorage = (cartState: CartState) => {
  try {
    const cartData = {
      items: cartState.items,
      qrCode: cartState.qrCode,
      vendorId: cartState.vendorId,
      pendingOrderId: cartState.pendingOrderId,
      timestamp: Date.now()
    };
    localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(cartData));
    
    if (process.env.NODE_ENV === 'development') {
      console.log('💾 Cart saved to localStorage:', {
        itemCount: cartState.items.length,
        vendorId: cartState.vendorId,
        qrCode: cartState.qrCode,
        pendingOrderId: cartState.pendingOrderId
      });
    }
  } catch (error) {
    console.warn('Failed to save cart to localStorage:', error);
  }
};

// Helper function to load cart from localStorage
const loadCartFromStorage = (): CartState | null => {
  try {
    const stored = localStorage.getItem(CART_STORAGE_KEY);
    if (!stored) return null;
    
    const cartData = JSON.parse(stored);
    
    // Validate the structure
    if (!cartData || typeof cartData !== 'object') return null;
    
    // Check if cart is not too old (24 hours max)
    const maxAge = 24 * 60 * 60 * 1000; // 24 hours
    if (cartData.timestamp && (Date.now() - cartData.timestamp) > maxAge) {
      console.log('Cart data is too old, clearing from localStorage');
      localStorage.removeItem(CART_STORAGE_KEY);
      return null;
    }
    
    return {
      items: Array.isArray(cartData.items) ? cartData.items : [],
      qrCode: cartData.qrCode || null,
      vendorId: cartData.vendorId || null,
      pendingOrderId: cartData.pendingOrderId || null,
    };
  } catch (error) {
    console.warn('Failed to load cart from localStorage:', error);
    localStorage.removeItem(CART_STORAGE_KEY);
    return null;
  }
};

// Helper function to clear cart from localStorage
const clearCartFromStorage = () => {
  try {
    localStorage.removeItem(CART_STORAGE_KEY);
    
    if (process.env.NODE_ENV === 'development') {
      console.log('🗑️ Cart cleared from localStorage');
    }
  } catch (error) {
    console.warn('Failed to clear cart from localStorage:', error);
  }
};

const cartReducer = (state: CartState, action: CartAction): CartState => {
  if (process.env.NODE_ENV === 'development') {
    console.log('🔄 Cart Action:', action.type, { 
      currentItems: state.items.length, 
      vendorId: state.vendorId, 
      qrCode: state.qrCode 
    });
  }
  
  let newState: CartState;
  
  switch (action.type) {
    case 'ADD_ITEM': {
      console.log('🛒 CartContext: ADD_ITEM called with payload:', action.payload);
      console.log('🛒 CartContext: Current items before add:', state.items.map(item => ({
        itemId: item.menuItem.id,
        portionId: item.portionId,
        quantity: item.quantity
      })));
      
      // Check for existing item with same menuItem.id AND portionId
      const existingItem = state.items.find(
        item => item.menuItem.id === action.payload.menuItem.id &&
                item.portionId === action.payload.portionId
      );
      if (existingItem) {
        console.log('🛒 CartContext: ADD_ITEM - Replacing existing item:', {
          itemId: action.payload.menuItem.id,
          portionId: action.payload.portionId,
          oldQuantity: existingItem.quantity,
          newQuantity: action.payload.quantity,
          oldSpiceLevel: existingItem.requestedSpiceLevel,
          newSpiceLevel: action.payload.requestedSpiceLevel
        });
        newState = {
          ...state,
          items: state.items.map(item =>
            item.menuItem.id === action.payload.menuItem.id && 
            item.portionId === action.payload.portionId
              ? { ...item, 
                  quantity: action.payload.quantity, // Replace instead of add
                  // Update price in case it changed
                  price: action.payload.price,
                  // Update spice level if provided
                  requestedSpiceLevel: action.payload.requestedSpiceLevel !== undefined 
                    ? action.payload.requestedSpiceLevel 
                    : item.requestedSpiceLevel
                }
              : item
          ),
        };
      } else {
        console.log('🛒 CartContext: ADD_ITEM - Adding new item');
        newState = {
          ...state,
          items: [...state.items, action.payload],
        };
      }
      
      console.log('🛒 CartContext: Items after add:', newState.items.map(item => ({
        itemId: item.menuItem.id,
        portionId: item.portionId,
        quantity: item.quantity
      })));
      break;
    }

    case 'REMOVE_ITEM':
      console.log('🛒 CartContext: REMOVE_ITEM called for itemId:', action.payload.itemId);
      console.log('🛒 CartContext: Items before remove:', state.items.map(item => ({
        itemId: item.menuItem.id,
        portionId: item.portionId,
        quantity: item.quantity
      })));
      newState = {
        ...state,
        items: state.items.filter(item => item.menuItem.id !== action.payload.itemId),
      };
      console.log('🛒 CartContext: Items after remove:', newState.items.map(item => ({
        itemId: item.menuItem.id,
        portionId: item.portionId,
        quantity: item.quantity
      })));
      break;

    case 'UPDATE_QUANTITY':
      newState = {
        ...state,
        items: state.items.map(item =>
          item.menuItem.id === action.payload.itemId
            ? { ...item, quantity: action.payload.quantity }
            : item
        ),
      };
      break;

    case 'UPDATE_COMMENT':
      newState = {
        ...state,
        items: state.items.map(item =>
          item.menuItem.id === action.payload.itemId
            ? { ...item, comment: action.payload.comment }
            : item
        ),
      };
      break;

    case 'UPDATE_TOPPINGS':
      newState = {
        ...state,
        items: state.items.map(item =>
          item.menuItem.id === action.payload.itemId
            ? { ...item, toppings: action.payload.toppings }
            : item
        ),
      };
      break;
      
    case 'UPDATE_PRICE':
      newState = {
        ...state,
        items: state.items.map(item =>
          item.menuItem.id === action.payload.itemId
            ? { ...item, price: action.payload.price }
            : item
        ),
      };
      break;

    case 'CLEAR_CART':
      newState = {
        ...state,
        items: [],
        pendingOrderId: null
      };
      // Clear from localStorage when clearing cart
      clearCartFromStorage();
      break;

    case 'SET_QR_CODE': {
      const qrPayload = action.payload as { qrCode: string | null };
      if (state.qrCode !== qrPayload.qrCode) {
        newState = {
          items: [],
          qrCode: qrPayload.qrCode,
          vendorId: state.vendorId,
          pendingOrderId: null,
        };
        // Clear from localStorage when changing QR code
        clearCartFromStorage();
      } else {
        newState = {
          ...state,
          qrCode: qrPayload.qrCode,
        };
      }
      break;
    }

    case 'SET_VENDOR_ID': {
      const vendorPayload = action.payload as { vendorId: string | null };
      if (state.vendorId !== vendorPayload.vendorId) {
        newState = {
          items: [],
          qrCode: state.qrCode,
          vendorId: vendorPayload.vendorId,
          vendorName: state.vendorName,
          pendingOrderId: null,
        };
        // Clear from localStorage when changing vendor
        clearCartFromStorage();
      } else {
        newState = {
          ...state,
          vendorId: vendorPayload.vendorId,
        };
      }
      break;
    }
    
    case 'SET_VENDOR_CONTEXT': {
      const contextPayload = action.payload as { vendorId: string; vendorName: string; qrCode: string };
      newState = {
        ...state,
        vendorId: contextPayload.vendorId,
        vendorName: contextPayload.vendorName,
        qrCode: contextPayload.qrCode,
      };
      break;
    }

    case 'SET_PENDING_ORDER_ID':
      newState = { ...state, pendingOrderId: action.payload.orderId };
      break;
      
    case 'HYDRATE_FROM_ORDER': {
      const orderPayload = action.payload as Order & { vendorId?: string | null; qrCode?: string | null; orderId?: string | null };
      const { items, vendorId, qrCode, orderId } = orderPayload || {};
      newState = {
        ...state,
        items: Array.isArray(items) ? items : [],
        vendorId: vendorId ?? null,
        qrCode: qrCode ?? null,
        pendingOrderId: orderId ?? null,
      };
      break;
    }

    case 'INITIALIZE_FROM_STORAGE':
      newState = action.payload;
      break;

    default:
      return state;
  }

  // Save to localStorage after every state change (except for operations that handle their own storage)
  if (action.type !== 'CLEAR_CART' && 
      action.type !== 'INITIALIZE_FROM_STORAGE' && // Don't save when loading from storage
      !(action.type === 'SET_QR_CODE' && state.qrCode !== (action.payload as { qrCode: string | null }).qrCode) &&
      !(action.type === 'SET_VENDOR_ID' && state.vendorId !== (action.payload as { vendorId: string | null }).vendorId)) {
    saveCartToStorage(newState);
  }

  return newState;
};

const CartProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(cartReducer, {
    items: [],
    qrCode: null,
    vendorId: null,
    pendingOrderId: null,
  });
  const location = useLocation();
  const { token, user, guestSession } = useAuth();

  // Track last navigated path and its zone to correctly record previousPath per role
  const lastPathRef = React.useRef<string>(location.pathname);
  const zoneFor = (p: string): 'CUSTOMER' | 'VENDOR' | 'STEWARD' =>
    p.startsWith('/vendor/') || p.startsWith('/admin/') || p.startsWith('/vendors/')
      ? 'VENDOR'
      : p.startsWith('/steward/')
      ? 'STEWARD'
      : 'CUSTOMER';
  const lastRoleRef = React.useRef<'CUSTOMER' | 'VENDOR' | 'STEWARD'>(zoneFor(location.pathname));

  // Debug: Log CartProvider mount/unmount
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      console.log('🏪 CartProvider mounted');
    }
    return () => {
      if (process.env.NODE_ENV === 'development') {
        console.log('🏪 CartProvider unmounted');
      }
    };
  }, []);

  // Initialize cart from localStorage on mount - only once and only if truly empty
  const [hasInitialized, setHasInitialized] = React.useState(false);
  
  useEffect(() => {
    if (hasInitialized) return; // Prevent multiple initializations
    
    // Only initialize if cart is completely empty/uninitialized
    const isCompletelyEmpty = state.items.length === 0 && 
                              !state.vendorId && 
                              !state.qrCode && 
                              !state.pendingOrderId;
    
    if (isCompletelyEmpty) {
      const storedCart = loadCartFromStorage();
      if (storedCart && (storedCart.items.length > 0 || storedCart.vendorId || storedCart.qrCode)) {
        console.log('🔄 Initializing cart from localStorage:', {
          itemCount: storedCart.items.length,
          vendorId: storedCart.vendorId,
          qrCode: storedCart.qrCode,
          pendingOrderId: storedCart.pendingOrderId
        });
        dispatch({ type: 'INITIALIZE_FROM_STORAGE', payload: storedCart });
      }
    }
    setHasInitialized(true);
  }, [hasInitialized]); // Remove state dependencies to prevent re-runs

  const addToCart = useCallback((item: {
    id: number;
    name?: string;
    price: number;
    portionId?: number;
    portionName?: string;
    selectedToppings?: Topping[];
    freeToppingsCount?: number;
    requestedSpiceLevel?: SpiceLevel;
  }, quantity = 1) => {
    if (process.env.NODE_ENV === 'development') {
      console.log('🛒 Adding to cart:', {
        itemId: item.id,
        itemName: item.name,
        portionId: item.portionId,
        portionName: item.portionName,
        quantity,
        toppings: item.selectedToppings?.length || 0,
        spiceLevel: item.requestedSpiceLevel
      });
    }

    // Always use ADD_ITEM action - let the reducer handle existing items
    dispatch({
      type: 'ADD_ITEM',
      payload: {
        menuItem: { id: item.id, name: item.name, dietaryType: item.dietaryType },
        quantity,
        price: item.price,
        portionId: item.portionId,
        portionName: item.portionName,
        toppings: item.selectedToppings?.map(t => t.id) || [],
        selectedToppings: item.selectedToppings || [],
        freeToppingsCount: item.freeToppingsCount || 0,
        requestedSpiceLevel: item.requestedSpiceLevel,
      },
    });
  }, []); // No dependencies to prevent stale closures

  const removeFromCart = useCallback((itemId: number) => {
    console.log('🛒 Removing from cart:', itemId);
    dispatch({ type: 'REMOVE_ITEM', payload: { itemId } });
  }, []);

  const updateQuantity = useCallback((itemId: number, quantity: number) => {
    dispatch({ type: 'UPDATE_QUANTITY', payload: { itemId, quantity } });
  }, []);

  const updateItemComment = useCallback((itemId: number, comment: string) => {
    dispatch({ type: 'UPDATE_COMMENT', payload: { itemId, comment } });
  }, []);

  const updateItemToppings = useCallback((itemId: number, toppings: number[]) => {
    dispatch({ type: 'UPDATE_TOPPINGS', payload: { itemId, toppings } });
  }, []);

  const updateItemPrice = useCallback((itemId: number, price: number) => {
    dispatch({ type: 'UPDATE_PRICE', payload: { itemId, price } });
  }, []);

  const setPendingOrderId = useCallback((orderId: string | null) => {
    dispatch({ type: 'SET_PENDING_ORDER_ID', payload: { orderId } });
    if (orderId) {
      localStorage.setItem('pendingOrderId', orderId);
    } else {
      localStorage.removeItem('pendingOrderId');
    }
  }, []);

  const clearCart = useCallback(() => {
    dispatch({ type: 'CLEAR_CART' });
    localStorage.removeItem('pendingOrderId');
    // clearCartFromStorage() is called inside the reducer for CLEAR_CART
  }, []);

  const setVendorContext = useCallback((vendorId: number, vendorName: string, qrCode: string) => {
    dispatch({ 
      type: 'SET_VENDOR_CONTEXT', 
      payload: { 
        vendorId: vendorId.toString(), 
        vendorName, 
        qrCode 
      } 
    });
  }, []);

  const clearVendorContext = useCallback(async () => {
    // Prevent clearing if already empty (avoid unnecessary dispatch cycles)
    if (!state.vendorId && !state.qrCode && !state.pendingOrderId && state.items.length === 0) {
      if (process.env.NODE_ENV === 'development') {
        console.log('Vendor context already clear, skipping');
      }
      return Promise.resolve();
    }
    
    if (process.env.NODE_ENV === 'development') {
      console.log('CLEARING VENDOR CONTEXT - FULL RESET');
    }
    
    localStorage.removeItem('vendorId');
    localStorage.removeItem('qrCode');
    localStorage.removeItem('pendingOrderId');
    // Clear cart from localStorage
    clearCartFromStorage();
    
    dispatch({ type: 'CLEAR_CART' });
    dispatch({ type: 'SET_VENDOR_ID', payload: { vendorId: null } });
    dispatch({ type: 'SET_QR_CODE', payload: { qrCode: null } });
    dispatch({ type: 'SET_PENDING_ORDER_ID', payload: { orderId: null } });
    
    if (process.env.NODE_ENV === 'development') {
      const afterClear = {
        localStorageVendorId: localStorage.getItem('vendorId'),
        localStorageQrCode: localStorage.getItem('qrCode'),
        localStoragePendingOrderId: localStorage.getItem('pendingOrderId'),
        localStorageCart: localStorage.getItem(CART_STORAGE_KEY),
      };
      console.log('Vendor context cleared, localStorage state:', afterClear);
    }
    
    return Promise.resolve();
  }, [state.vendorId, state.qrCode, state.pendingOrderId, state.items.length]);

  const hydrateCartFromOrder = useCallback((order: Order) => {
    if (!order || !order.items) return;
    
    console.log('Hydrating cart from order:', order.id, 'status:', order.status);
    
    if (order.vendor?.id) {
      dispatch({ type: 'SET_VENDOR_ID', payload: { vendorId: order.vendor.id.toString() } });
      localStorage.setItem('vendorId', order.vendor.id.toString());
    }
    if (order.qrCode?.code) {
      dispatch({ type: 'SET_QR_CODE', payload: { qrCode: order.qrCode.code } });
      localStorage.setItem('qrCode', order.qrCode.code);
    }
    
    dispatch({
      type: 'HYDRATE_FROM_ORDER',
      payload: {
        items: order.items.map((item: OrderItem) => ({
          menuItem: { id: item.menuItem.id },
          quantity: item.quantity,
          price: item.price,
          toppings: item.toppings?.map((t: OrderTopping) => t.id) || [],
          comment: item.comment || '',
        })),
        vendorId: order.vendor?.id?.toString() || null,
        qrCode: order.qrCode?.code || null,
        orderId: order.id?.toString() || null,
      },
    });
    
    // Set pending order ID for both PENDING and ACCEPTED orders (both can be modified)
    if (order.status === 'PENDING' || order.status === 'ACCEPTED') {
      localStorage.setItem('pendingOrderId', order.id?.toString() || '');
      dispatch({ type: 'SET_PENDING_ORDER_ID', payload: { orderId: order.id?.toString() || null } });
    }
  }, []);

  const getCartQuantity = useCallback((itemId: number, portionId?: number) => {
    return state.items
      .filter(item => {
        if (portionId !== undefined) {
          return item.menuItem.id === itemId && item.portionId === portionId;
        }
        return item.menuItem.id === itemId;
      })
      .reduce((sum, item) => sum + item.quantity, 0);
  }, [state.items]);

  const getTotalItems = useCallback(() => {
    return state.items.reduce((sum, item) => sum + item.quantity, 0);
  }, [state.items]);

  const isCartValid = useCallback(() => {
    return state.items.length > 0;
  }, [state.items]);

  const getCartTotal = useCallback(() => {
    return state.items.reduce((total, item) => {
      let itemTotal = item.price * item.quantity;

      // Add topping charges with free topping logic
      if (item.selectedToppings && item.selectedToppings.length > 0) {
        // Sort toppings by price descending
        const sortedToppings = [...item.selectedToppings].sort((a, b) => b.price - a.price);

        // Apply free count to highest priced items
        const freeToppingsCount = item.freeToppingsCount || 0;
        const paidToppings = sortedToppings.slice(freeToppingsCount);

        // Calculate topping charge (per item quantity)
        const toppingCharge = paidToppings.reduce((sum, t) => sum + t.price, 0);
        itemTotal += toppingCharge * item.quantity;
      }

      return total + itemTotal;
    }, 0);
  }, [state.items]);

  const getItemCount = useCallback(() => {
    return state.items.length;
  }, [state.items]);

  // Edge dual-screen till: mirror the cart to the customer-facing screen via
  // BroadcastChannel. Inert unless VITE_EDGE_MODE === 'true'.
  useCustomerDisplayBroadcast(state.items, getCartTotal);

  // Sync URL parameters with context - only when on vendor/menu pages
  useEffect(() => {
    const isVendorMenu = /^\/menu\/[0-9]+/.test(location.pathname);
    const isMenuWithQr = location.pathname === '/menu' && location.search.includes('qr=');
    
    // Only sync URL params when on relevant pages
    if (!isVendorMenu && !isMenuWithQr) return;

    const params = new URLSearchParams(location.search);
    const qrCode = params.get('qr');
    const vendorId = location.pathname.split('/').find(segment => 
      segment.match(/^\d+$/)
    ) || null;
    
    // Only dispatch if values are actually different and not null
    if (qrCode && qrCode !== state.qrCode) {
      console.log('Syncing QR code from URL to context:', qrCode);
      dispatch({ type: 'SET_QR_CODE', payload: { qrCode } });
      localStorage.setItem('qrCode', qrCode);
    }
    
    if (vendorId && vendorId !== state.vendorId) {
      console.log('Syncing vendor ID from URL to context:', vendorId);
      dispatch({ type: 'SET_VENDOR_ID', payload: { vendorId } });
      localStorage.setItem('vendorId', vendorId);
    }
  }, [location.pathname, location.search, state.qrCode, state.vendorId, dispatch]);
  
  useEffect(() => {
    // Record the immediate previous path per role (zone), not the current path
    const currentPath = location.pathname;
    const blocked = new Set(['/login', '/login', '/login', '/signup']);
    const isTransient = (p: string) => p === '/qr';

    const currentRole = zoneFor(currentPath);
    const prevPath = lastPathRef.current;
    const prevRole = lastRoleRef.current;

    // Detect role-scoped back navigation to avoid pushing during back transitions
    const backFlagKey = `navBack:${currentRole}`;
    let isBackNav = false;
    try {
      const backObj = JSON.parse(sessionStorage.getItem(backFlagKey) || 'null');
      if (backObj && backObj.to === currentPath) {
        isBackNav = true;
      }
    } catch {
      isBackNav = false;
    }
    // Clear back flag once observed
    if (isBackNav) {
      sessionStorage.removeItem(backFlagKey);
    }

    // Only store previous path when staying within the same zone, previous path is meaningful, and not during back nav
    if (
      prevPath &&
      prevPath !== currentPath &&
      prevRole === currentRole &&
      !blocked.has(prevPath) &&
      !isTransient(prevPath) &&
      !prevPath.includes('redirect') &&
      !isBackNav
    ) {
      localStorage.setItem(`previousPath:${currentRole}`, prevPath);
    }

    // Update refs for next navigation
    lastPathRef.current = currentPath;
    lastRoleRef.current = currentRole;

    // Also maintain a role-scoped stack of visited paths for multi-step back
    const stackKey = `navStack:${currentRole}`;
    let stack: string[] = [];
    try {
      stack = JSON.parse(sessionStorage.getItem(stackKey) || '[]');
      if (!Array.isArray(stack)) stack = [];
    } catch {
      stack = [];
    }
    // Push previous path to role stack when appropriate (not during back nav)
    if (
      prevPath &&
      prevPath !== currentPath &&
      prevRole === currentRole &&
      !blocked.has(prevPath) &&
      !isTransient(prevPath) &&
      !prevPath.includes('redirect') &&
      !isBackNav
    ) {
      // Avoid duplicates if it's already at the top
      if (stack.length === 0 || stack[stack.length - 1] !== prevPath) {
        stack.push(prevPath);
        // Limit stack size to a reasonable number
        if (stack.length > 25) {
          stack = stack.slice(stack.length - 25);
        }
        sessionStorage.setItem(stackKey, JSON.stringify(stack));
      }
    }
  }, [location.pathname]);
  
  // Only restore from localStorage on initial mount, after initialization
  useEffect(() => {
    if (!hasInitialized) return;
    
    const savedVendorId = localStorage.getItem('vendorId');
    const savedQrCode = localStorage.getItem('qrCode');
    const savedPendingOrderId = localStorage.getItem('pendingOrderId');
    
    if (savedVendorId && !state.vendorId) {
      dispatch({ type: 'SET_VENDOR_ID', payload: { vendorId: savedVendorId } });
    }
    
    if (savedQrCode && !state.qrCode) {
      dispatch({ type: 'SET_QR_CODE', payload: { qrCode: savedQrCode } });
    }
    
    if (savedPendingOrderId && !state.pendingOrderId) {
      dispatch({ type: 'SET_PENDING_ORDER_ID', payload: { orderId: savedPendingOrderId } });
    }
  }, [hasInitialized, state.vendorId, state.qrCode, state.pendingOrderId, dispatch]);

  // Track navigation state to prevent loops
  const [lastClearPath, setLastClearPath] = React.useState<string>('');
  
  useEffect(() => {
    const currentPath = location.pathname + location.search;
    
    // Define pages that should preserve vendor context
    const isVendorSpecificPage = /^\/menu\/[0-9]+/.test(location.pathname) || /^\/menu\?/.test(currentPath);
    const isCheckoutPage = location.pathname === '/checkout';
    const isOrderDetailPage = /^\/orders\/\d+/.test(location.pathname);
    const isOrderSuccessPage = /^\/order-success\/\d+/.test(location.pathname);
    const isOrdersListPage = location.pathname === '/orders';
    const isHistoryPage = location.pathname === '/history';
    const isProfilePage = location.pathname === '/profile';
    
    // Pages that should preserve context
    const shouldPreserveContext = isVendorSpecificPage || 
                                  isCheckoutPage || 
                                  isOrderDetailPage || 
                                  isOrderSuccessPage ||
                                  isOrdersListPage ||
                                  isHistoryPage ||
                                  isProfilePage;
    
    // Only clear on specific pages that truly need fresh start
    const isQrPage = location.pathname === '/qr';
    const isScanPage = location.pathname === '/scan';
    const isHomePage = location.pathname === '/';
    const isGenericMenuPage = location.pathname === '/menu' && !location.search;
    
    const needsToClear = (isQrPage || isScanPage || isHomePage || isGenericMenuPage) && 
                         !shouldPreserveContext &&
                         (state.vendorId || state.qrCode || state.pendingOrderId) &&
                         lastClearPath !== currentPath; // Prevent duplicate clears
    
    if (process.env.NODE_ENV === 'development') {
      console.log('Navigation check:', { 
        path: currentPath,
        shouldPreserveContext, 
        needsToClear,
        hasVendorContext: Boolean(state.vendorId || state.qrCode),
        vendorId: state.vendorId,
        qrCode: state.qrCode,
        lastClearPath
      });
    }
    
    if (needsToClear) {
      console.log('Clearing vendor context due to navigation to:', location.pathname);
      setLastClearPath(currentPath);
      clearVendorContext();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- clearVendorContext excluded to prevent infinite re-render loop (BUG-07)
  }, [location.pathname, location.search, state.vendorId, state.qrCode, state.pendingOrderId, lastClearPath]);

  // Track previous authentication state to detect logout vs login
  interface AuthUser {
    id: number;
    username?: string;
    email?: string;
  }
  
  interface GuestSession {
    id: string;
    sessionToken?: string;
  }
  
  const [prevAuthState, setPrevAuthState] = React.useState<{token: string | null, user: AuthUser | null, guestSession: GuestSession | null}>({
    token,
    user,
    guestSession
  });

  useEffect(() => {
    const currentAuthState = { token, user, guestSession };
    
    // Only clear cart if user was previously authenticated and now is not
    // This prevents clearing cart on login, only on logout
    const wasAuthenticated = Boolean(prevAuthState.token && (prevAuthState.user || prevAuthState.guestSession));
    const isAuthenticated = Boolean(token && (user || guestSession));
    
    if (wasAuthenticated && !isAuthenticated) {
      console.log('User logged out, clearing vendor context');
      clearVendorContext();
    }
    
    setPrevAuthState(currentAuthState);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, user, guestSession, clearVendorContext]);

  const [isHydrating, setIsHydrating] = React.useState(false);
  const [hasHydrated, setHasHydrated] = React.useState(false);
  const [clearedOrderIds, setClearedOrderIds] = React.useState<Set<string>>(new Set());
  
  useEffect(() => {
    setHasHydrated(false);
    setClearedOrderIds(new Set());
  }, [state.vendorId, state.qrCode]);
  
  const clearCartAfterOrderPlacement = useCallback((orderId: string) => {
    console.log('Clearing cart after successful order placement:', orderId);
    
    dispatch({ type: 'CLEAR_CART' });
    localStorage.removeItem('pendingOrderId');
    // clearCartFromStorage() is called inside the reducer for CLEAR_CART
    
    setClearedOrderIds(prev => new Set(prev).add(orderId));
    setHasHydrated(true);
  }, []);
  
  useEffect(() => {
    const currentPath = location.pathname;
    const isMenuPage = currentPath.startsWith('/menu');
    const isCheckoutPage = currentPath === '/checkout';
    const shouldHydrate = isMenuPage || isCheckoutPage;
    
    const pendingOrderId = state.pendingOrderId || localStorage.getItem('pendingOrderId');
    const needsHydration = shouldHydrate && 
                          !hasHydrated && 
                          state.items.length === 0 && 
                          pendingOrderId &&
                          !clearedOrderIds.has(pendingOrderId) &&
                          !isHydrating;
    
    if (needsHydration) {
      console.log('Fetching pending order for hydration:', pendingOrderId);
      setIsHydrating(true);
      
      api.get(`/orders/${pendingOrderId}`)
        .then(res => {
          const order = res.data;
          console.log('Pending order response:', order);
          
          if (order && (order.status === 'PENDING' || order.status === 'ACCEPTED') && Array.isArray(order.items) && order.items.length > 0) {
            if (!clearedOrderIds.has(order.id.toString())) {
              console.log('Hydrating cart from pending order:', order.id);
              hydrateCartFromOrder(order);
            }
          } else {
            console.log('Order is not pending or has no items, clearing pending order ID');
            setPendingOrderId(null);
          }
          setHasHydrated(true);
        })
        .catch(err => {
          console.log('Error fetching pending order:', err);
          if (err.response && err.response.status === 404) {
            console.log('Pending order not found, clearing pending order ID');
            setPendingOrderId(null);
          }
          setHasHydrated(true);
        })
        .finally(() => {
          setIsHydrating(false);
        });
    }
  }, [location.pathname, state.items.length, state.pendingOrderId, hasHydrated, clearedOrderIds, isHydrating, hydrateCartFromOrder, setPendingOrderId]);

  // Unused function - keeping for potential future use
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const updatePendingOrder = useCallback(async () => {
    if (!state.pendingOrderId || state.items.length === 0) return;
    
    try {
      if (process.env.NODE_ENV === 'development') {
        console.log('Updating pending order:', state.pendingOrderId);
      }
      
      const orderData = {
        items: state.items.map(item => ({
          menuItem: { id: item.menuItem.id },
          quantity: item.quantity,
          price: item.price,
          toppings: (item.toppings || []).map(toppingId => ({ id: toppingId })),
          comment: item.comment || ''
        })),
        deliveryType: 'DINE_IN'
      };
      
      const response = await api.put(`/orders/${state.pendingOrderId}`, orderData);
      if (process.env.NODE_ENV === 'development') {
        console.log('Pending order updated successfully:', response.data);
      }
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Failed to update pending order:', error);
      }
      
      const errorResponse = error as { response?: { status?: number } };
      if (errorResponse?.response?.status === 404) {
        console.log('Clearing stale pending order ID due to 404');
        setPendingOrderId(null);
      }
    }
  }, [state.pendingOrderId, state.items, setPendingOrderId]);
  
  // DISABLED: Automatic pending order updates during modification
  // This was causing immediate backend calls when modifying orders
  // Updates should only happen when user explicitly submits the order
  /*
  useEffect(() => {
    if (state.pendingOrderId && state.items.length > 0 && hasHydrated) {
      const timeoutId = setTimeout(() => {
        updatePendingOrder();
      }, 1000);
      
      return () => clearTimeout(timeoutId);
    }
  }, [state.items, state.pendingOrderId, hasHydrated, updatePendingOrder]);
  */

  return (
    <CartContext.Provider
      value={{
        state,
        addToCart,
        removeFromCart,
        updateQuantity,
        updateItemComment,
        updateItemToppings,
        updateItemPrice,
        clearCart,
        getCartQuantity,
        getTotalItems,
        isCartValid,
        clearVendorContext,
        setVendorContext,
        hydrateCartFromOrder,
        setPendingOrderId,
        clearCartAfterOrderPlacement,
        getCartTotal,
        getItemCount,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

const useCart = () => {
  const context = useContext(CartContext);
  if (context === undefined) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

// Export all hooks and components
export { CartProvider, useCart };
